import React, { useId, useLayoutEffect, useState } from "react";
import { Box, ButtonBase, Divider, IconButton, Popover, Slide, Slider, Switch, Tooltip, Typography, useMediaQuery } from "@mui/material";
import { BrightnessHighOutlined, CloseRounded, DarkModeOutlined, ExpandMoreRounded, RestartAltRounded, SettingsOutlined, TextFieldsRounded, ViewSidebarOutlined } from "@mui/icons-material";
import { useMovimentoReduzido, usePreferencias } from "../contextos/PreferenciasContext";
import { useFonte } from "../contextos/FonteContext";
import { configuracaoFonte, configuracaoLuminosidade } from "../config/preferencias";
import { estilosPreferenciasRecolhiveis as estilos, painelPreferenciasRecolhiveis } from "../estilos/preferencias";

function TextoPreferencia({ titulo, descricao }) {
  return <Box sx={estilos.textoLinha}>
    <Typography component="span" sx={{ display: "block", ...estilos.tituloLinha }}>{titulo}</Typography>
    {descricao && <Typography component="span" sx={{ display: "block", ...estilos.descricaoLinha }}>{descricao}</Typography>}
  </Box>;
}

function ControleRecolhivel({ titulo, Icone, valor, configuracao, onChange, disabled = false }) {
  const [aberto, setAberto] = useState(false);
  const id = useId();
  const valorId = `${id}-valor`;

  return (
    <Box component="section" aria-label={titulo} data-aberto={aberto} sx={estilos.ajuste}>
      <ButtonBase
        aria-label={titulo}
        aria-describedby={valorId}
        aria-expanded={aberto}
        aria-controls={aberto ? id : undefined}
        disabled={disabled}
        onClick={() => setAberto((atual) => !atual)}
        sx={estilos.linhaAjuste}
      >
        <Box sx={estilos.iconeLinha}><Icone /></Box>
        <Typography component="span" sx={{ ...estilos.tituloLinha, flex: 1, minWidth: 0 }}>{titulo}</Typography>
        <Typography id={valorId} component="output" sx={estilos.percentual}>{valor}%</Typography>
        <ExpandMoreRounded sx={{ ...estilos.seta, transform: aberto ? "rotate(180deg)" : "none" }} />
      </ButtonBase>
      {aberto && (
        <Box id={id} sx={estilos.conteudoAjuste}>
          <Slider
            aria-label={titulo}
            min={configuracao.minimo}
            max={configuracao.maximo}
            step={configuracao.passo}
            value={valor}
            disabled={disabled}
            onChange={(_, novoValor) => onChange(novoValor)}
            getAriaValueText={(novoValor) => `${novoValor}%`}
            sx={estilos.barra}
          />
        </Box>
      )}
    </Box>
  );
}

export default function PreferenciasMenu() {
  const [anchor, setAnchor] = useState(null);
  const [topo, setTopo] = useState(12);
  const movimentoReduzido = useMovimentoReduzido();

  // Acompanha a altura do cabeçalho quando a janela ou a fonte mudam.
  useLayoutEffect(() => {
    if (!anchor) return undefined;
    const cabecalho = anchor.closest("header") || anchor;
    const atualizarTopo = () => setTopo(Math.max(12, Math.ceil(cabecalho.getBoundingClientRect().bottom) + 12));
    atualizarTopo();
    const observador = new ResizeObserver(atualizarTopo);
    observador.observe(cabecalho);
    window.addEventListener("resize", atualizarTopo);
    return () => {
      observador.disconnect();
      window.removeEventListener("resize", atualizarTopo);
    };
  }, [anchor]);
  const id = useId();
  const { tema, luminosidade, menuInicial, alterarPreferencias, restaurarPreferencias } = usePreferencias();
  const { percentual, habilitado, definirFonte, restaurarFonte } = useFonte();
  const telaPequena = useMediaQuery("(max-width:899px)");
  const fechar = () => setAnchor(null);
  const definirMenuInicial = (expandido) => {
    alterarPreferencias({ menuInicial: expandido ? "expandido" : "recolhido" });
    if (telaPequena) fechar();
  };

  return <>
    <Tooltip title="Preferências" arrow>
      <IconButton aria-label="Abrir Preferências" aria-haspopup="dialog" aria-expanded={Boolean(anchor)} aria-controls={anchor ? id : undefined}
        color="inherit" onClick={(event) => setAnchor(event.currentTarget)} sx={{ width: 40, height: 40, borderRadius: 1 }}>
        <SettingsOutlined sx={{ fontSize: 26 }} />
      </IconButton>
    </Tooltip>
    <Popover id={id} anchorEl={anchor} anchorReference="none" open={Boolean(anchor)} onClose={fechar}
      TransitionComponent={Slide} TransitionProps={{ direction: "left" }} transitionDuration={movimentoReduzido ? 0 : 180}
      slotProps={{ paper: { role: "dialog", "aria-label": "Preferências", sx: [painelPreferenciasRecolhiveis, { top: topo, maxHeight: `calc(100dvh - ${topo + 12}px)` }] } }}>
      <Box sx={{ p: 1.25 }}>
        <Box sx={estilos.cabecalho}>
          <Box sx={estilos.iconeCabecalho}><SettingsOutlined sx={{ fontSize: 22 }} /></Box>
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography component="h2" sx={estilos.tituloCabecalho}>Preferências</Typography>
          </Box>
          <IconButton autoFocus aria-label="Fechar Preferências" onClick={fechar} sx={estilos.fechar}>
            <CloseRounded sx={{ fontSize: 18 }} />
          </IconButton>
        </Box>
        <Box component="label" sx={estilos.linha}>
          <Box sx={estilos.iconeLinha}><ViewSidebarOutlined /></Box>
          <TextoPreferencia titulo="Menu lateral" />
          <Switch checked={menuInicial === "expandido"} sx={estilos.switch}
            inputProps={{ "aria-label": "Menu lateral", "aria-description": "Desligado: recolhido. Ligado: expandido. Aplica imediatamente e salva para os próximos acessos." }}
            onChange={(event) => definirMenuInicial(event.target.checked)} />
        </Box>
        <Box component="label" sx={estilos.linha}>
          <Box sx={estilos.iconeLinha}><DarkModeOutlined /></Box>
          <TextoPreferencia titulo="Tema escuro" />
          <Switch checked={tema === "escuro"} sx={estilos.switch}
            inputProps={{ "aria-label": "Tema", "aria-description": "Desligado: tema claro. Ligado: tema escuro." }}
            onChange={(event) => alterarPreferencias({ tema: event.target.checked ? "escuro" : "claro" })} />
        </Box>
        <ControleRecolhivel key={`luminosidade-${Boolean(anchor)}`} titulo="Luminosidade" Icone={BrightnessHighOutlined} valor={luminosidade} configuracao={configuracaoLuminosidade}
          onChange={(valor) => alterarPreferencias({ luminosidade: valor })} />
        <ControleRecolhivel key={`fonte-${Boolean(anchor)}`} titulo="Tamanho da fonte" Icone={TextFieldsRounded} valor={percentual} configuracao={configuracaoFonte}
          onChange={definirFonte} disabled={!habilitado} />
        <Divider sx={estilos.divisor} />
        <ButtonBase aria-label="Restaurar preferências" sx={estilos.restaurar} onClick={() => { restaurarPreferencias(); restaurarFonte(); fechar(); }}>
          <Box sx={estilos.iconeRestaurar}><RestartAltRounded /></Box>
          <TextoPreferencia titulo="Restaurar preferências" />
        </ButtonBase>
      </Box>
    </Popover>
  </>;
}
